import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react'

interface ChatBubbleState {
	isVisible: boolean
	mode: 'floating' | 'fullscreen'
	activeConversationId?: string
	activeOrderId?: string
	bubblePosition?: { bottom: number; right: number }
	isOrderPanelOpen?: boolean
}

interface ChatBubbleContextType {
	state: ChatBubbleState
	showBubble: () => void
	hideBubble: () => void
	openInbox: () => void
	openFloatingWithOrder: (orderId: string, position?: { bottom: number; right: number }) => void
	closeAll: () => void
	setActiveConversation: (conversationId: string) => void
	setOrderPanelOpen: (isOpen: boolean) => void
}

const ChatBubbleContext = createContext<ChatBubbleContextType | undefined>(undefined)

interface ChatBubbleProviderProps {
	children: ReactNode
}

export function ChatBubbleProvider({ children }: ChatBubbleProviderProps) {
	const [state, setState] = useState<ChatBubbleState>({
		isVisible: true,
		mode: 'floating',
		activeConversationId: undefined,
		activeOrderId: undefined,
		bubblePosition: undefined,
		isOrderPanelOpen: false
	})

	// Load saved state on mount
	useEffect(() => {
		const savedState = localStorage.getItem('chatBubbleState')
		if (savedState) {
			try {
				const parsed = JSON.parse(savedState)
				setState(prevState => ({ ...prevState, ...parsed }))
			} catch (error) {
				console.error('Failed to parse saved chat bubble state:', error)
			}
		}
	}, [])

	// Save state changes to localStorage
	useEffect(() => {
		localStorage.setItem('chatBubbleState', JSON.stringify(state))
	}, [state])


	const showBubble = useCallback(() => {
		setState(prev => ({ ...prev, isVisible: true }))
	}, [])

	const hideBubble = useCallback(() => {
		setState(prev => ({ ...prev, isVisible: false }))
	}, [])

	const openInbox = useCallback(() => {
		setState(prev => ({
			...prev,
			mode: 'floating',
			isVisible: true
		}))
	}, [])

	const openFloatingWithOrder = useCallback((orderId: string, position?: { bottom: number; right: number }) => {
		setState(prev => ({
			...prev,
			mode: 'floating' as const,
			isVisible: true,
			activeOrderId: orderId,
			bubblePosition: position || prev.bubblePosition
		}))
		
		// Dispatch event to open the floating modal
		window.dispatchEvent(new CustomEvent('openFloatingInbox'))
	}, [])

	const closeAll = useCallback(() => {
		setState(prev => ({
			...prev,
			mode: 'floating',
			isVisible: true, // Keep bubble visible
			activeConversationId: undefined,
			activeOrderId: undefined,
			bubblePosition: { bottom: 12, right: 12 } // Reset to default position
		}))
	}, [])


	const setActiveConversation = useCallback((conversationId: string) => {
		setState(prev => ({
			...prev,
			activeConversationId: conversationId
		}))
	}, [])

	const setOrderPanelOpen = useCallback((isOpen: boolean) => {
		setState(prev => ({
			...prev,
			isOrderPanelOpen: isOpen
		}))
	}, [])

	const value: ChatBubbleContextType = useMemo(() => ({
		state,
		showBubble,
		hideBubble,
		openInbox,
		openFloatingWithOrder,
		closeAll,
		setActiveConversation,
		setOrderPanelOpen
	}), [state, showBubble, hideBubble, openInbox, openFloatingWithOrder, closeAll, setActiveConversation, setOrderPanelOpen])

	return (
		<ChatBubbleContext.Provider value={value}>
			{children}
		</ChatBubbleContext.Provider>
	)
}

export function useChatBubble() {
	const context = useContext(ChatBubbleContext)
	if (context === undefined) {
		throw new Error('useChatBubble must be used within a ChatBubbleProvider')
	}
	return context
}
